import React, { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSort,
  faSortUp,
  faSortDown,
  faLock,
} from "@fortawesome/pro-solid-svg-icons";
import AdvTaskListingUserDetailDto from "../../../../types/dtos/dashboards/manager/listings/adv-tasks/AdvTaskListingUserDetailDto";
import AdvTaskListingDto from "../../../../types/dtos/dashboards/manager/listings/adv-tasks/AdvTaskListingDto";
import cx from "classnames";
import Avatar from "../../../common/Avatar";
import dateHelper from "../../../../helpers/dateHelper";
import PaginationControls from "../../../common/PaginationControls";
import { ClientTaskType } from "../../../../types/dtos/tasks/advanced-tasks/ClientTaskType";
import { listingHelper } from "../../../../helpers/listingHelper";
import Tooltip from "../../../common/Tooltip";
import ExistingAdvancedTaskPopup from "../../../tasks/advanced-tasks/ExistingAdvancedTaskPopup";
import Toast from "../../../common/Toast";
import useTaskManagement from "../../../../hooks/useTaskManagement";
import { ToastConfig } from "../../../common/ToastConfig";

interface AdvTaskTableViewProps {
  users: AdvTaskListingUserDetailDto[] | null;
  tasks: AdvTaskListingDto[] | null;
  taskType: ClientTaskType | null;
  onTaskRefreshNeeded: (userId: number) => void;
  pageNumber: number;
  setPageNumber: (pageNumber: number) => void;
}

// Define sort column types
type SortColumn = "NAME" | "TITLE" | "CATEGORY" | "DUE_DATE" | "STATUS";
type SortDirection = "ASC" | "DESC";

const AdvTaskTableView: React.FC<AdvTaskTableViewProps> = ({
  users,
  tasks,
  taskType,
  onTaskRefreshNeeded,
  pageNumber,
  setPageNumber,
}) => {
  const { t } = useTranslation();
  // Set default sort column to STATUS
  const [sortColumn, setSortColumn] = useState<SortColumn>("STATUS");
  const [sortDirection, setSortDirection] = useState<SortDirection>("ASC");
  const [pageSize, setPageSize] = useState(10);

  // Define custom status sort order - prioritize tasks that need action
  const getStatusSortOrder = useCallback((status: string): number => {
    switch (status) {
      case "OVERDUE":
        return 1; // Highest priority - needs immediate attention
      case "ACTIVE":
        return 2; // Second priority - currently being worked on
      case "COMPLETED":
        return 3; // Third priority - finished tasks
      case "CANCELLED":
        return 4; // Fourth priority - cancelled tasks
      case "HISTORIC":
        return 5; // Lowest priority - archived tasks
      default:
        return 999; // Unknown statuses go to the end
    }
  }, []);

  // Use the task management hook instead of local state
  const taskManagement = useTaskManagement(taskType, (userId) => {
    // When a task is updated, call the parent onTaskClick to refresh data
    if (taskManagement.selectedTaskId) {
      onTaskRefreshNeeded(userId);
    }
  });

  // Update comment count for a task
  const handleCommentCountChanged = (
    taskId: string,
    newCommentCount: number
  ) => {
    if (!tasks) return;

    // Find the task in the tasks array
    const taskToUpdate = tasks.find((task) => task.userTaskId === taskId);
    if (taskToUpdate) {
      taskToUpdate.commentCount = newCommentCount;
      // Force a re-render
      setPageNumber(pageNumber);
    }
  };

  // Get user by id
  const getUserById = useCallback(
    (userId: number): AdvTaskListingUserDetailDto | undefined => {
      return users?.find((user) => user.employeeId === userId);
    },
    [users]
  );

  // Handle sort column change
  const handleSortColumnChange = useCallback(
    (column: SortColumn) => {
      let newDirection: SortDirection = "ASC";

      if (column === sortColumn) {
        // Toggle direction if same column
        newDirection = sortDirection === "ASC" ? "DESC" : "ASC";
      }

      setSortColumn(column);
      setSortDirection(newDirection);
      // Reset to first page when sorting changes
      setPageNumber(1);
    },
    [sortColumn, sortDirection, setPageNumber]
  );

  // Get sort icon
  const getSortIcon = useCallback(
    (column: SortColumn) => {
      return (
        <>
          {column !== sortColumn && (
            <FontAwesomeIcon icon={faSort} className="text-gray-300 pt-1" />
          )}
          {column === sortColumn && sortDirection === "ASC" && (
            <FontAwesomeIcon icon={faSortUp} className="text-gray-500 pt-1" />
          )}
          {column === sortColumn && sortDirection === "DESC" && (
            <FontAwesomeIcon icon={faSortDown} className="text-gray-500 pt-1" />
          )}
        </>
      );
    },
    [sortColumn, sortDirection]
  );

  // Sort tasks
  const sortedTasks = React.useMemo(() => {
    if (!tasks) return [];

    return [...tasks].sort((a, b) => {
      const multiplier = sortDirection === "ASC" ? 1 : -1;
      let compareResult = 0;

      switch (sortColumn) {
        case "NAME": {
          const userA = getUserById(a.ownerEmployeeId);
          const userB = getUserById(b.ownerEmployeeId);
          const nameA = userA?.employeeFullName || "";
          const nameB = userB?.employeeFullName || "";
          compareResult = nameA.localeCompare(nameB);

          // If names are equal, sort by target date as secondary criterion
          if (compareResult === 0) {
            const dateA = a.targetDate ? new Date(a.targetDate).getTime() : 0;
            const dateB = b.targetDate ? new Date(b.targetDate).getTime() : 0;
            return dateA - dateB; // Always sort dates in ascending order for same names
          }
          break;
        }
        case "TITLE":
          compareResult = a.title.localeCompare(b.title);
          break;
        case "CATEGORY": {
          const categoryA = a.categoryName || "";
          const categoryB = b.categoryName || "";
          compareResult = categoryA.localeCompare(categoryB);
          break;
        }
        case "DUE_DATE": {
          const dateA = a.targetDate ? new Date(a.targetDate).getTime() : 0;
          const dateB = b.targetDate ? new Date(b.targetDate).getTime() : 0;
          compareResult = dateA - dateB;
          break;
        }
        case "STATUS": {
          // Use custom status sort order instead of alphabetical
          const statusOrderA = getStatusSortOrder(a.overallStatus);
          const statusOrderB = getStatusSortOrder(b.overallStatus);
          compareResult = statusOrderA - statusOrderB;
          break;
        }
      }

      // Apply the multiplier for ASC/DESC direction
      compareResult *= multiplier;

      // If primary sort criteria are equal, use consistent secondary criteria
      if (compareResult === 0) {
        // For non-NAME sort columns, sort by name and then target date as tie-breakers
        if (sortColumn !== "NAME") {
          // First try to differentiate by name
          const userA = getUserById(a.ownerEmployeeId);
          const userB = getUserById(b.ownerEmployeeId);
          const nameA = userA?.employeeFullName || "";
          const nameB = userB?.employeeFullName || "";
          const nameCompare = nameA.localeCompare(nameB);

          if (nameCompare !== 0) {
            return nameCompare;
          }

          // Then by target date
          const dateA = a.targetDate ? new Date(a.targetDate).getTime() : 0;
          const dateB = b.targetDate ? new Date(b.targetDate).getTime() : 0;
          const dateCompare = dateA - dateB;

          if (dateCompare !== 0) {
            return dateCompare;
          }
        }

        // If all else fails, use a stable sort with userTaskId
        return a.userTaskId.localeCompare(b.userTaskId);
      }

      return compareResult;
    });
  }, [tasks, sortColumn, sortDirection, getUserById, getStatusSortOrder]);

  // Paginate tasks
  const paginatedTasks = React.useMemo(() => {
    // Create a map to ensure we only include each task once
    const uniqueTasks = new Map();

    // Generate a unique key for each task
    sortedTasks.forEach((task) => {
      if (!uniqueTasks.has(task.userTaskId)) {
        uniqueTasks.set(task.userTaskId, task);
      }
    });

    // Convert map values back to array for pagination
    const dedupedTasks = Array.from(uniqueTasks.values());

    // Apply pagination
    const startIndex = (pageNumber - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return dedupedTasks.slice(startIndex, endIndex);
  }, [sortedTasks, pageNumber, pageSize]);

  // Format date
  const formatDate = useCallback((dateString: Date | null | undefined) => {
    if (!dateString) return "N/A";
    return dateHelper
      .storedTargetDateAsLocalCalendarDate(dateString)
      .toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
  }, []);

  // Handle task click
  const handleTaskClick = (task: AdvTaskListingDto) => {
    // Get the user who owns this task
    const user = getUserById(task.ownerEmployeeId);

    // Get the user's active categories
    const userCategories = user?.activeCategories || [];

    // Open task popup using the hook
    taskManagement.openTaskPopup(
      task.userTaskId,
      userCategories,
      false,
      task.ownerEmployeeId
    );
  };

  if (!users || !tasks) {
    return (
      <div className="p-4 text-center text-gray-500">No data available.</div>
    );
  }

  // Column width styles - using fixed pixel widths for more aggressive control
  const columnStyles = {
    title: { width: "30%", maxWidth: "30%", minWidth: "30%" },
    category: { width: "15%", maxWidth: "15%", minWidth: "15%" },
    name: { width: "23%", maxWidth: "23%", minWidth: "23%" },
    dueDate: { width: "11%", maxWidth: "11%", minWidth: "11%" },
    status: { width: "10%", maxWidth: "10%", minWidth: "10%" },
    actions: { width: "11%", maxWidth: "11%", minWidth: "11%" },
  };

  // Render the table rows
  const renderTableRows = () => {
    if (!paginatedTasks.length) return null;

    return paginatedTasks.map((task) => {
      const user = getUserById(task.ownerEmployeeId);

      // Get status color
      const statusColor = listingHelper.getTaskStatusColor(task);

      return (
        <tr
          key={task.userTaskId}
          className={`hover:bg-gray-50 border-b ${listingHelper.getManagerBorderClass(
            task
          )}`}
        >
          <td className="px-2 sm:px-3 py-3 sm:py-4" style={columnStyles.title}>
            <div className="flex items-center">
              <div className="truncate">
                <span
                  className="font-medium text-xs sm:text-sm cursor-pointer hover:underline transition-colors duration-200"
                  onClick={(e) => {
                    e.stopPropagation(); // Prevent row click
                    handleTaskClick(task);
                  }}
                  title="Click to view/update task"
                >
                  {task.title}
                </span>
              </div>
            </div>
          </td>
          <td
            className="px-2 sm:px-3 py-3 sm:py-4 hidden sm:table-cell"
            style={columnStyles.category}
          >
            <div className="truncate text-xs sm:text-sm">
              {task.categoryName ? t(task.categoryName) : ""}
            </div>
          </td>
          <td
            className="py-3 sm:py-4 pl-2 sm:pl-6 pr-2 sm:pr-3 text-xs sm:text-sm"
            style={columnStyles.name}
          >
            <div className="flex items-center">
              <div className="hidden sm:block">
                <Avatar
                  imageUrl={user?.profileImageUrl}
                  userFullName={user?.employeeFullName || ""}
                  userInitials={user?.initials || ""}
                  size={6}
                  activeStatus="DO-NOT-SHOW"
                />
              </div>
              <div className="sm:hidden">
                <Avatar
                  imageUrl={user?.profileImageUrl}
                  userFullName={user?.employeeFullName || ""}
                  userInitials={user?.initials || ""}
                  size={6}
                  activeStatus="DO-NOT-SHOW"
                />
              </div>
              <div
                className="ml-2 sm:ml-3"
                style={{ maxWidth: "calc(100% - 30px)" }}
              >
                <div className="font-medium text-gray-900 truncate text-xs sm:text-sm">
                  {user?.employeeFullName || "Unknown"}
                </div>
              </div>
            </div>
          </td>
          <td
            className="px-2 sm:px-3 py-3 sm:py-4 hidden sm:table-cell"
            style={columnStyles.dueDate}
          >
            <div className="text-xs sm:text-sm">
              {formatDate(task.targetDate)}
            </div>
          </td>
          <td className="px-2 sm:px-3 py-3 sm:py-4" style={columnStyles.status}>
            <div className="flex items-center justify-center">
              <span
                className={`text-xs px-1.5 sm:px-2 py-0.5 rounded-full ${statusColor}`}
              >
                {listingHelper.getTaskStatusText(task)}
              </span>
            </div>
          </td>
          <td
            className="px-2 sm:px-3 py-3 sm:py-4 text-right hidden sm:table-cell"
            style={columnStyles.actions}
          >
            <button
              onClick={(e) => {
                e.stopPropagation(); // Prevent row click
                handleTaskClick(task);
              }}
              className="btn-primary text-xs sm:text-sm py-1 px-2 sm:px-3 text-center"
            >
              {task.overallStatus === "COMPLETED" ||
              task.overallStatus === "CANCELLED" ||
              task.overallStatus === "HISTORIC"
                ? t("Common.View")
                : t("Common.Update")}
            </button>
          </td>
        </tr>
      );
    });
  };

  return (
    <div className="mt-4">
      {/* Toast notification */}
      {taskManagement.toastConfig.show && (
        <Toast
          title={taskManagement.toastConfig.title}
          message={taskManagement.toastConfig.message}
          type={taskManagement.toastConfig.type}
          duration={5000}
          onClose={() => taskManagement.setToastConfig(ToastConfig.hidden())}
        />
      )}

      {paginatedTasks.length === 0 ? (
        <div className="p-4 text-center text-gray-500">No tasks found.</div>
      ) : (
        <>
          <div className="overflow-x-auto rounded-lg border border-gray-200 shadow-sm bg-white">
            <table className="w-full table-fixed divide-y divide-gray-200">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th
                    scope="col"
                    className="px-2 sm:px-3 py-2 sm:py-3.5 text-left text-xs sm:text-sm font-semibold text-gray-900 cursor-pointer group"
                    onClick={() => handleSortColumnChange("TITLE")}
                    style={columnStyles.title}
                  >
                    <div className="flex items-center gap-1 sm:gap-2">
                      <span className="whitespace-nowrap">Title</span>
                      <span className="ml-auto">{getSortIcon("TITLE")}</span>
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="px-2 sm:px-3 py-2 sm:py-3.5 text-left text-xs sm:text-sm font-semibold text-gray-900 cursor-pointer group hidden sm:table-cell"
                    onClick={() => handleSortColumnChange("CATEGORY")}
                    style={columnStyles.category}
                  >
                    <div className="flex items-center gap-1 sm:gap-2">
                      <span className="whitespace-nowrap">Category</span>
                      <span className="ml-auto">{getSortIcon("CATEGORY")}</span>
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="py-2 sm:py-3.5 pl-2 sm:pl-6 pr-2 sm:pr-3 text-left text-xs sm:text-sm font-semibold text-gray-900 cursor-pointer group"
                    onClick={() => handleSortColumnChange("NAME")}
                    style={columnStyles.name}
                  >
                    <div className="flex items-center gap-1 sm:gap-2">
                      <span className="whitespace-nowrap">
                        {t("Pages.Admin.Common.Name")}
                      </span>
                      <span className="ml-auto">{getSortIcon("NAME")}</span>
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="px-2 sm:px-3 py-2 sm:py-3.5 text-left text-xs sm:text-sm font-semibold text-gray-900 cursor-pointer group hidden sm:table-cell"
                    onClick={() => handleSortColumnChange("DUE_DATE")}
                    style={columnStyles.dueDate}
                  >
                    <div className="flex items-center gap-1 sm:gap-2">
                      <span className="whitespace-nowrap">Due Date</span>
                      <span className="ml-auto">{getSortIcon("DUE_DATE")}</span>
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="px-2 sm:px-3 py-2 sm:py-3.5 text-xs sm:text-sm font-semibold text-gray-900 cursor-pointer group"
                    onClick={() => handleSortColumnChange("STATUS")}
                    style={columnStyles.status}
                  >
                    <div className="flex items-center gap-1 sm:gap-2">
                      <span className="whitespace-nowrap flex-1 text-center">
                        Status
                      </span>
                      <span className="ml-auto">{getSortIcon("STATUS")}</span>
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="px-2 sm:px-3 py-2 sm:py-3.5 text-xs sm:text-sm font-semibold text-gray-900 hidden sm:table-cell"
                    style={columnStyles.actions}
                  >
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {renderTableRows()}
              </tbody>
            </table>
          </div>

          <PaginationControls
            activePage={pageNumber}
            itemsPerPage={pageSize}
            totalItemCount={sortedTasks.length}
            isLoading={false}
            loadingText={t("Common.Loading") + "..."}
            onPageChange={setPageNumber}
            onPageSizeChange={setPageSize}
          />

          {/* Task popup */}
          {taskType && taskManagement.selectedTaskId && (
            <ExistingAdvancedTaskPopup
              usageScenario="DASHBOARD-TASKS"
              taskType={taskType}
              userTaskId={taskManagement.selectedTaskId}
              categories={taskManagement.selectedUserCategories || []}
              isOpen={taskManagement.isExistingTaskPopupOpen}
              isLocked={taskManagement.isSelectedTaskLocked}
              onOpenChange={taskManagement.handleTaskPopupOpenChange}
              onSuccessfulUpdate={taskManagement.handleSuccessfulUpdate}
              onSuccessfulCancel={taskManagement.handleSuccessfulCancel}
              onSuccessfulClose={taskManagement.handleSuccessfulClose}
              onSuccessfulReOpen={taskManagement.handleSuccessfulReOpen}
              onSuccessfulSavedForLater={
                taskManagement.handleSuccessfulSavedForLater
              }
              onCommentCountChanged={handleCommentCountChanged}
            />
          )}
        </>
      )}
    </div>
  );
};

export default AdvTaskTableView;
